import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createProductFuse } from '../utils/search';

// Highlight the first occurrence of the query within a field.
function highlight(text, query) {
  const t = text || '';
  const q = (query || '').trim();
  if (!q) return t;
  const idx = t.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return t;
  return (
    <>
      {t.slice(0, idx)}
      <mark>{t.slice(idx, idx + q.length)}</mark>
      {t.slice(idx + q.length)}
    </>
  );
}

const MAX_SUGGESTIONS = 6;

function SearchBar({ products, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);

  const fuse = useMemo(() => createProductFuse(products), [products]);

  const suggestions = useMemo(() => {
    const q = (value || '').trim();
    if (!q) return [];
    return fuse.search(q, { limit: MAX_SUGGESTIONS }).map(r => r.item);
  }, [fuse, value]);

  // Reset the highlighted row whenever the query changes.
  useEffect(() => { setActiveIndex(-1); }, [value]);

  // Close the dropdown on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (product) => {
    onChange(product.description);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0) { e.preventDefault(); pick(suggestions[activeIndex]); }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="header-search" ref={containerRef}>
      <div className="search-container">
        <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          type="text"
          className="search-input"
          placeholder="Search by product name or SKU..."
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          aria-label="Search products"
          autoComplete="off"
        />
        {value && (
          <button
            type="button"
            className="search-clear"
            onClick={() => { onChange(''); setOpen(false); }}
            aria-label="Clear search"
          >
            &times;
          </button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <ul className="search-suggestions">
          {suggestions.map((p, i) => (
            <li
              key={p.id}
              className={`search-suggestion ${i === activeIndex ? 'active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(p); }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <span className="suggestion-sku">{highlight(p.sku, value)}</span>
              <span className="suggestion-desc">{highlight(p.description, value)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SearchBar;
